import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { getApiErrorMessages } from '../lib/utils';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const timeoutsRef = useRef(new Map());

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        const timeout = timeoutsRef.current.get(id);
        if (timeout) {
            clearTimeout(timeout);
            timeoutsRef.current.delete(id);
        }
    }, []);

    const toast = useCallback(
        ({ title, description, variant = 'default', durationMs = 3000 }) => {
            const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            const t = {
                id,
                title: title || '',
                description: description || '',
                variant,
            };
            setToasts((prev) => [t, ...prev].slice(0, 12));

            const timeout = setTimeout(() => dismiss(id), durationMs);
            timeoutsRef.current.set(id, timeout);

            return id;
        },
        [dismiss]
    );

    const toastApiError = useCallback(
        (error, { title = '', fallback = 'Ocorreu um erro.', durationMs = 9000 } = {}) => {
            const messages = getApiErrorMessages(error, fallback);
            const base = Date.now();
            const newToasts = messages.map((description, index) => ({
                id: `${base}-${index}-${Math.random().toString(16).slice(2)}`,
                title: index === 0 ? title : '',
                description,
                variant: 'error',
            }));

            setToasts((prev) => [...newToasts, ...prev].slice(0, 12));

            newToasts.forEach((t) => {
                const timeout = setTimeout(() => dismiss(t.id), durationMs);
                timeoutsRef.current.set(t.id, timeout);
            });
        },
        [dismiss]
    );

    const value = useMemo(() => ({ toast, toastApiError, dismiss, toasts }), [toast, toastApiError, dismiss, toasts]);

    return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return ctx;
}

